import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/useSEO';
import {
  Shield,
  Mail,
  Globe2,
  Calendar,
  ArrowRight,
  ArrowUpRight,
  ListChecks,
  Lock,
  FileText,
  Sparkles,
  GraduationCap,
  ChevronRight,
} from 'lucide-react';

/**
 * PrivacyPolicyPage — `/study-abroad/privacy-policy/`
 * ───────────────────────────────────────────────────────────────────────
 * Dedicated Study Abroad Privacy Policy.
 *
 * Visual identity matches the rest of the Study Abroad domain:
 *   navy hero (#003087 → #001F5C → #020617) + #C5A028 gold accent
 *   + #FACC15 highlight + amber-tinted Trust Box.
 *
 * Structure:
 *   1. Hero (Effective + Last Updated, MEA badge, CTA chips)
 *   2. Sticky Table of Contents (anchor nav, 13 sections)
 *   3. 13 numbered policy sections — section cards, lists, tables
 *   4. Contact panel (studyabroad@zafco.in + Study Abroad URL)
 *   5. Final CTA banner
 */

const EFFECTIVE_DATE = 'January 1, 2026';
const LAST_UPDATED = 'June 5, 2026';
const SA_EMAIL = 'studyabroad@zafco.in';
const SA_URL = 'https://zafco.in/study-abroad/';

const TOC = [
  { id: 'overview', label: '01 · Overview' },
  { id: 'information-we-collect', label: '02 · Information We Collect' },
  { id: 'how-we-use', label: '03 · How We Use Your Information' },
  { id: 'cookies', label: '04 · Cookies, Analytics & Advertising' },
  { id: 'legal-basis', label: '05 · Legal Basis for Processing' },
  { id: 'sharing', label: '06 · Information Sharing' },
  { id: 'international-transfers', label: '07 · International Data Transfers' },
  { id: 'security', label: '08 · Data Security' },
  { id: 'retention', label: '09 · Data Retention' },
  { id: 'rights', label: '10 · Your Privacy Rights' },
  { id: 'contact', label: '11 · Contact Information' },
  { id: 'changes', label: '12 · Policy Changes' },
  { id: 'consent', label: '13 · Consent' },
];

const VOLUNTARY_INFO = [
  'Full name',
  'Email address',
  'Mobile number',
  'Postal address',
  'Date of birth',
  'Academic qualifications and transcripts',
  'Language proficiency test scores (IELTS, TOEFL, PTE, GRE, GMAT, etc.)',
  'Passport information and nationality details',
  'Study preferences and target countries',
  'Visa and travel history relevant to your application',
  'Financial or sponsorship information required for admission or visa assessment',
  'Any information included in forms, enquiries, statements of purpose, resumes, or supporting documents',
];

const AUTO_INFO = [
  'IP address',
  'Browser type and device information',
  'Operating system information',
  'Pages visited and navigation patterns',
  'Referral sources',
  'Session and interaction data',
  'Cookie and analytics information',
];

const USES = [
  'Assessing eligibility for overseas education opportunities',
  'Providing university shortlisting and admissions guidance',
  'Supporting student visa application processes',
  'Providing scholarship and funding guidance',
  'Responding to enquiries and consultation requests',
  'Communicating application updates and service information',
  'Improving website performance and user experience',
  'Maintaining website security and preventing misuse',
  'Complying with applicable legal, regulatory, and compliance requirements',
];

const ANALYTICS_TOOLS = [
  'Google Analytics',
  'Google Ads Conversion Tracking',
  'Website performance and security monitoring tools',
];

const LEGAL_BASES = [
  'Your consent',
  'Performance of requested services',
  'Legitimate business interests',
  'Compliance with legal or regulatory obligations',
];

const SHARING_PARTIES = [
  'Universities and educational institutions',
  'Admission partners and authorized representatives',
  'Government agencies, embassies, visa authorities, and verification bodies',
  'Professional advisers and compliance personnel',
  'Technology and cloud service providers supporting our operations under appropriate confidentiality obligations',
];

const SECURITY_MEASURES = [
  'Secure website encryption (SSL/TLS)',
  'Access controls and restricted permissions',
  'Secure storage environments',
  'Periodic monitoring and security reviews',
  'Staff confidentiality and privacy awareness measures',
];

const RETENTION_PURPOSES = [
  'Deliver requested services',
  'Meet legal, regulatory, or compliance obligations',
  'Resolve disputes',
  'Maintain legitimate business records',
];

const RIGHTS = [
  'Request access to your personal information',
  'Request correction of inaccurate information',
  'Request deletion of information where legally permissible',
  'Withdraw consent where processing is based on consent',
  'Object to certain forms of processing',
  'Request information regarding how your data is used',
];

const SectionHeading = ({ eyebrow, title, subtitle }) => (
  <div className="max-w-3xl mb-7 lg:mb-9">
    {eyebrow && (
      <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3 bg-[#C5A028]/12 border border-[#C5A028]/35 text-[#C5A028] text-[10px] font-bold tracking-[0.22em] uppercase">
        {eyebrow}
      </div>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-[32px] font-bold font-syne leading-tight mb-3 text-[#003087]">
      {title}
    </h2>
    {subtitle && (
      <p className="leading-relaxed text-sm sm:text-base text-slate-600">{subtitle}</p>
    )}
  </div>
);

const BulletList = ({ items, testid }) => (
  <ul className="space-y-2.5" data-testid={testid}>
    {items.map((item) => (
      <li key={item} className="flex items-start gap-2.5">
        <ChevronRight className="w-4 h-4 mt-0.5 shrink-0 text-[#C5A028]" aria-hidden="true" />
        <span className="text-sm sm:text-[15px] text-slate-700 leading-relaxed">{item}</span>
      </li>
    ))}
  </ul>
);

const PolicySection = ({ id, number, title, children }) => (
  <section
    id={id}
    aria-labelledby={`${id}-heading`}
    className="scroll-mt-28 rounded-2xl bg-white border border-slate-200/80 hover:border-[#C5A028]/40 transition-colors p-6 sm:p-8 shadow-sm"
    data-testid={`sa-privacy-section-${id}`}
  >
    <div className="flex items-start gap-4 mb-5">
      <div className="shrink-0 w-11 h-11 rounded-xl bg-[#003087]/8 border border-[#003087]/15 flex items-center justify-center">
        <span className="font-syne font-bold text-[#003087] text-sm">{number}</span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase mb-1">
          Section {number}
        </div>
        <h3
          id={`${id}-heading`}
          className="text-lg sm:text-xl lg:text-2xl font-bold font-syne text-[#003087] leading-tight"
        >
          {title}
        </h3>
      </div>
    </div>
    <div className="space-y-4 text-[15px] text-slate-700 leading-relaxed zaf-prose">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  usePageMeta(
    'Privacy Policy | ZAFCO Study Abroad',
    'Read the dedicated Privacy Policy for ZAFCO Study Abroad. Learn how we collect, use, store and protect your information when you enquire, request a profile evaluation, or use our overseas education advisory services.',
    { canonical: '/study-abroad/privacy-policy/' },
  );

  // BreadcrumbList + PrivacyPolicy schema (auto-inject + cleanup on unmount)
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Study Abroad', item: 'https://zafco.in/study-abroad/' },
        { '@type': 'ListItem', position: 3, name: 'Privacy Policy' },
      ],
    };
    const policySchema = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': 'https://zafco.in/study-abroad/privacy-policy/#webpage',
      name: 'ZAFCO Study Abroad Privacy Policy',
      url: 'https://zafco.in/study-abroad/privacy-policy/',
      isPartOf: { '@id': 'https://zafco.in/#website' },
      inLanguage: 'en-IN',
      datePublished: '2026-01-01',
      dateModified: '2026-06-05',
      publisher: { '@id': 'https://zafco.in/#organization' },
      about: {
        '@type': 'Thing',
        name: 'Personal data handling for overseas education advisory services',
      },
    };
    const bcEl = document.createElement('script');
    const policyEl = document.createElement('script');
    bcEl.type = 'application/ld+json';
    bcEl.setAttribute('data-sa-privacy-bc', '1');
    bcEl.text = JSON.stringify(breadcrumb);
    policyEl.type = 'application/ld+json';
    policyEl.setAttribute('data-sa-privacy-schema', '1');
    policyEl.text = JSON.stringify(policySchema);
    document.head.appendChild(bcEl);
    document.head.appendChild(policyEl);
    return () => {
      document.head.querySelector('script[data-sa-privacy-bc]')?.remove();
      document.head.querySelector('script[data-sa-privacy-schema]')?.remove();
    };
  }, []);

  const tocItems = useMemo(() => TOC, []);

  return (
    <div className="bg-white zaf-prose" data-testid="sa-privacy-policy-page">
      {/* ============ HERO ============ */}
      <section
        className="relative overflow-hidden text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 55%, #020617 100%)' }}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-32 -right-24 w-96 h-96 rounded-full bg-[#C5A028]/14 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-emerald-500/8 blur-3xl"
        />
        <div className="relative max-w-7xl mx-auto px-6 py-14 lg:py-24">
          <nav aria-label="Breadcrumb" className="mb-6 text-xs sm:text-[13px] text-slate-300/85">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-[#FACC15]">Home</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li>
                <Link to="/study-abroad/" className="hover:text-[#FACC15]">Study Abroad</Link>
              </li>
              <li aria-hidden="true">/</li>
              <li className="text-white/95">Privacy Policy</li>
            </ol>
          </nav>

          <div className="max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 backdrop-blur-sm text-[10.5px] font-bold tracking-[0.22em] text-[#FACC15] uppercase">
                <Sparkles className="w-3.5 h-3.5" /> Legal · Compliance
              </span>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-[10.5px] font-bold tracking-[0.22em] text-emerald-200 uppercase">
                <Shield className="w-3.5 h-3.5" /> Study Abroad Division
              </span>
            </div>

            <h1
              className="text-3xl sm:text-4xl lg:text-[54px] font-bold font-syne leading-[1.05] tracking-tight mb-5"
              data-testid="sa-privacy-h1"
            >
              Privacy Policy
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-slate-300 leading-relaxed max-w-2xl mb-6">
              How <strong className="text-white">ZAFCO Study Abroad</strong> collects, uses, stores, and
              protects your information across enquiries, profile evaluations, and overseas education
              advisory services.
            </p>

            <div className="flex flex-wrap gap-3" data-testid="sa-privacy-dates">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/8 border border-white/15 text-xs sm:text-sm">
                <Calendar className="w-4 h-4 text-[#FACC15]" aria-hidden="true" />
                <span className="text-slate-300">Effective Date:</span>
                <span className="font-bold text-white" data-testid="sa-privacy-effective">
                  {EFFECTIVE_DATE}
                </span>
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/35 text-xs sm:text-sm">
                <FileText className="w-4 h-4 text-[#FACC15]" aria-hidden="true" />
                <span className="text-slate-300">Last Updated:</span>
                <span className="font-bold text-white" data-testid="sa-privacy-updated">
                  {LAST_UPDATED}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="bg-slate-50 border-y border-slate-200/60 py-5">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-[10px] uppercase tracking-[0.25em] text-slate-500 font-bold">
            Dedicated Study Abroad Privacy Notice
          </p>
          <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-slate-600">
            <a
              href={`mailto:${SA_EMAIL}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#C5A028]/40 hover:text-[#003087] transition-colors"
              data-testid="sa-privacy-trust-email"
            >
              <Mail className="w-3.5 h-3.5 text-[#C5A028]" /> {SA_EMAIL}
            </a>
            <a
              href={SA_URL}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-[#C5A028]/40 hover:text-[#003087] transition-colors"
              data-testid="sa-privacy-trust-url"
            >
              <Globe2 className="w-3.5 h-3.5 text-[#C5A028]" /> {SA_URL}
            </a>
          </div>
        </div>
      </section>

      {/* ============ MAIN — TOC + CONTENT ============ */}
      <section className="bg-white py-12 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[260px_1fr] gap-10 lg:gap-14">
          {/* TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start" aria-label="Table of contents">
            <div
              className="rounded-2xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-white p-5"
              data-testid="sa-privacy-toc"
            >
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-200/70">
                <ListChecks className="w-4 h-4 text-[#C5A028]" aria-hidden="true" />
                <div className="text-[10px] font-bold tracking-[0.22em] text-[#003087] uppercase">
                  Table of Contents
                </div>
              </div>
              <nav>
                <ol className="space-y-1.5">
                  {tocItems.map((item) => (
                    <li key={item.id}>
                      <a
                        href={`#${item.id}`}
                        className="block text-[13px] text-slate-600 hover:text-[#003087] hover:bg-white rounded-md px-2 py-1.5 transition-colors leading-snug"
                        data-testid={`sa-privacy-toc-link-${item.id}`}
                      >
                        {item.label}
                      </a>
                    </li>
                  ))}
                </ol>
              </nav>
            </div>
          </aside>

          {/* Sections */}
          <div className="space-y-7 lg:space-y-9 min-w-0">
            {/* 01 Overview */}
            <PolicySection id="overview" number="01" title="Overview">
              <p>
                Welcome to <strong className="text-[#003087]">ZAFCO Study Abroad</strong>, accessible
                through{' '}
                <a
                  href={SA_URL}
                  className="text-[#003087] underline decoration-[#C5A028]/60 underline-offset-2 hover:text-[#C5A028]"
                >
                  {SA_URL}
                </a>
                . ZAFCO Study Abroad is the specialized overseas education advisory division of{' '}
                <strong className="text-[#003087]">M/s Zafco Human Resource Management</strong>, an
                organization engaged in international recruitment, overseas education guidance, and
                related consulting services.
              </p>
              <p>
                We value your privacy and are committed to handling your personal information
                responsibly, transparently, and securely. This Privacy Policy explains how we
                collect, use, store, process, and protect information when you visit our website,
                submit an enquiry, request a profile evaluation, or use our study abroad consulting
                services.
              </p>
              <p>
                By accessing our website or submitting your information, you acknowledge and agree
                to the practices described in this Privacy Policy.
              </p>
            </PolicySection>

            {/* 02 Information We Collect */}
            <PolicySection id="information-we-collect" number="02" title="Information We Collect">
              <p>
                We may collect information directly from you as well as certain information
                automatically when you use our website.
              </p>

              <div className="rounded-xl border border-slate-200/80 overflow-hidden">
                <div className="bg-[#003087]/5 px-5 py-3 border-b border-slate-200/80">
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#003087] uppercase">
                    A · Information You Provide Voluntarily
                  </div>
                </div>
                <div className="p-5">
                  <p className="mb-4 text-slate-600">
                    Depending on the services requested, you may provide:
                  </p>
                  <BulletList items={VOLUNTARY_INFO} testid="sa-privacy-voluntary-list" />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200/80 overflow-hidden">
                <div className="bg-[#003087]/5 px-5 py-3 border-b border-slate-200/80">
                  <div className="text-[10px] font-bold tracking-[0.22em] text-[#003087] uppercase">
                    B · Information Collected Automatically
                  </div>
                </div>
                <div className="p-5">
                  <p className="mb-4 text-slate-600">When you visit our website, we may collect:</p>
                  <BulletList items={AUTO_INFO} testid="sa-privacy-auto-list" />
                </div>
              </div>
            </PolicySection>

            {/* 03 How We Use */}
            <PolicySection id="how-we-use" number="03" title="How We Use Your Information">
              <p>We may use your information for the following purposes:</p>
              <BulletList items={USES} testid="sa-privacy-uses-list" />
            </PolicySection>

            {/* 04 Cookies */}
            <PolicySection
              id="cookies"
              number="04"
              title="Cookies, Analytics and Advertising Technologies"
            >
              <p>
                Our website may use cookies and similar technologies to improve functionality,
                measure performance, and understand visitor interactions. We may use services such
                as:
              </p>
              <BulletList items={ANALYTICS_TOOLS} testid="sa-privacy-analytics-list" />
              <p>
                These technologies help us understand how visitors interact with our website and
                improve our services. We do{' '}
                <strong className="text-[#003087]">not</strong> intentionally transmit personally
                identifiable information such as names, email addresses, phone numbers, passport
                details, or financial documents to advertising platforms for advertising purposes.
              </p>
              <p>
                You may control cookies through your browser settings and may opt out of certain
                advertising personalization features offered by third-party providers, including
                Google.
              </p>
            </PolicySection>

            {/* 05 Legal basis */}
            <PolicySection id="legal-basis" number="05" title="Legal Basis for Processing">
              <p>Where applicable, we process information based on:</p>
              <BulletList items={LEGAL_BASES} testid="sa-privacy-legal-list" />
              <p>
                For international applicants, we endeavor to follow widely accepted privacy and
                data protection principles, including transparency, purpose limitation, data
                minimization, and appropriate security safeguards.
              </p>
            </PolicySection>

            {/* 06 Sharing */}
            <PolicySection id="sharing" number="06" title="Information Sharing">
              <p>
                We do <strong className="text-[#003087]">not sell, rent, or trade</strong> personal
                information to third parties. Information may be shared only when reasonably
                necessary for the services requested, including:
              </p>
              <BulletList items={SHARING_PARTIES} testid="sa-privacy-sharing-list" />
              <p>Information is shared only to the extent reasonably required for the relevant purpose.</p>
            </PolicySection>

            {/* 07 International transfers */}
            <PolicySection
              id="international-transfers"
              number="07"
              title="International Data Transfers"
            >
              <p>
                Because overseas education services often involve institutions and authorities
                located outside India, your information may be transferred internationally as part
                of admissions, verification, scholarship, or visa-related processes. Where
                appropriate, we take reasonable measures to protect information during such
                transfers.
              </p>
            </PolicySection>

            {/* 08 Security */}
            <PolicySection id="security" number="08" title="Data Security">
              <p>
                We implement industry-standard administrative, technical, and organizational
                safeguards designed to protect personal information against unauthorized access,
                misuse, alteration, disclosure, or destruction. These measures may include:
              </p>
              <BulletList items={SECURITY_MEASURES} testid="sa-privacy-security-list" />
              <div className="rounded-xl border-l-4 border-[#C5A028] bg-amber-50/60 px-5 py-4 text-[14px] text-slate-700 flex items-start gap-3">
                <Lock className="w-5 h-5 text-[#C5A028] mt-0.5 shrink-0" aria-hidden="true" />
                <span>
                  While we take reasonable precautions, no system can guarantee absolute security.
                </span>
              </div>
            </PolicySection>

            {/* 09 Retention */}
            <PolicySection id="retention" number="09" title="Data Retention">
              <p>We retain information only for as long as reasonably necessary to:</p>
              <BulletList items={RETENTION_PURPOSES} testid="sa-privacy-retention-list" />
              <p>
                Information that is no longer required may be securely deleted, anonymized, or
                archived in accordance with applicable requirements.
              </p>
            </PolicySection>

            {/* 10 Rights */}
            <PolicySection id="rights" number="10" title="Your Privacy Rights">
              <p>Subject to applicable laws, you may have the right to:</p>
              <BulletList items={RIGHTS} testid="sa-privacy-rights-list" />
              <p>Requests may be submitted using the contact details below.</p>
            </PolicySection>

            {/* 11 Contact */}
            <PolicySection id="contact" number="11" title="Contact Information">
              <p>For privacy-related enquiries, requests, or concerns, please contact:</p>
              <div
                className="rounded-2xl border-2 border-[#C5A028]/30 bg-gradient-to-br from-amber-50/40 via-white to-amber-50/40 p-6 shadow-md"
                data-testid="sa-privacy-contact-card"
              >
                <div className="flex items-start gap-3 mb-4 pb-4 border-b border-amber-200/60">
                  <div className="w-11 h-11 rounded-full bg-[#C5A028]/15 border border-[#C5A028]/40 flex items-center justify-center shrink-0">
                    <GraduationCap className="w-5 h-5 text-[#C5A028]" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.22em] text-[#C5A028] uppercase">
                      Privacy & Compliance · Study Abroad Division
                    </div>
                    <div className="text-base font-bold font-syne text-[#003087]">
                      ZAFCO Study Abroad
                    </div>
                    <div className="text-sm text-slate-600">
                      M/s Zafco Human Resource Management
                    </div>
                  </div>
                </div>

                <dl className="grid sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <dt className="text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-1">
                      Email
                    </dt>
                    <dd>
                      <a
                        href={`mailto:${SA_EMAIL}`}
                        className="inline-flex items-center gap-2 text-[#003087] font-semibold hover:text-[#C5A028] transition-colors"
                        data-testid="sa-privacy-contact-email"
                      >
                        <Mail className="w-4 h-4 text-[#C5A028]" aria-hidden="true" />
                        {SA_EMAIL}
                      </a>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-1">
                      Website
                    </dt>
                    <dd>
                      <a
                        href={SA_URL}
                        className="inline-flex items-center gap-2 text-[#003087] font-semibold hover:text-[#C5A028] transition-colors break-all"
                        data-testid="sa-privacy-contact-url"
                      >
                        <Globe2 className="w-4 h-4 text-[#C5A028]" aria-hidden="true" />
                        {SA_URL}
                      </a>
                    </dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-[10px] font-bold tracking-[0.22em] text-slate-500 uppercase mb-1">
                      Registered Office
                    </dt>
                    <dd className="text-slate-700">
                      Zafco House
                      <br />
                      New Delhi, India
                    </dd>
                  </div>
                </dl>
              </div>
              <p className="text-[14px] text-slate-600">
                We will make reasonable efforts to respond promptly and in accordance with
                applicable laws and regulations.
              </p>
            </PolicySection>

            {/* 12 Changes */}
            <PolicySection id="changes" number="12" title="Policy Changes">
              <p>
                We may update this Privacy Policy periodically to reflect changes in legal
                requirements, business practices, technology, or services. The{' '}
                <strong className="text-[#003087]">&ldquo;Last Updated&rdquo;</strong> date at the
                top of this page indicates when this Privacy Policy was most recently revised.
              </p>
            </PolicySection>

            {/* 13 Consent */}
            <PolicySection id="consent" number="13" title="Consent">
              <p>
                By accessing our website, submitting an enquiry, requesting a consultation, or
                using our services, you acknowledge that you have read and understood this Privacy
                Policy and consent to the collection and use of information as described herein.
              </p>
            </PolicySection>

            {/* Footer attribution */}
            <div
              className="rounded-2xl border border-slate-200/80 bg-slate-50/70 px-6 py-5 text-center text-xs sm:text-sm text-slate-500"
              data-testid="sa-privacy-attribution"
            >
              &copy; {new Date().getFullYear()} ZAFCO Study Abroad &middot; A Division of M/s Zafco
              Human Resource Management &middot; All Rights Reserved.
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section
        className="text-white"
        style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}
      >
        <div className="max-w-4xl mx-auto px-6 py-14 lg:py-20 text-center" data-testid="sa-privacy-final-cta">
          <Shield className="w-12 h-12 text-[#FACC15] mx-auto mb-5" aria-hidden="true" />
          <h2 className="text-2xl md:text-4xl font-bold font-syne mb-4 leading-tight">
            Questions about your data?{' '}
            <span className="text-[#FACC15]">We&apos;re happy to help.</span>
          </h2>
          <p className="text-slate-300 max-w-2xl mx-auto mb-7 leading-relaxed">
            Reach our Study Abroad privacy team directly — for access, correction, deletion, or any
            other privacy enquiry.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a
              href={`mailto:${SA_EMAIL}`}
              className="inline-flex items-center justify-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C5A028]/30"
              data-testid="sa-privacy-final-email-cta"
            >
              Email {SA_EMAIL} <ArrowUpRight className="w-4 h-4" />
            </a>
            <Link
              to="/study-abroad/book-counselling/?source=study-abroad-privacy-policy"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white border border-white/20 font-semibold px-7 py-3.5 rounded-full transition-colors"
              data-testid="sa-privacy-final-counselling-cta"
            >
              Book Free Counselling <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
